// src/components/CookieConsent.jsx

import React from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';

const Banner = styled.div`
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 9999;
  background: var(--card);
  border-top: 1px solid var(--bdr);
  box-shadow: 0 -4px 20px rgba(0, 0, 0, 0.15);
  padding: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 20px;
  animation: slideUp 0.3s ease;

  @keyframes slideUp {
    from {
      transform: translateY(100%);
    }
    to {
      transform: translateY(0);
    }
  }

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 12px;
    padding: 16px;
  }
`;

const Content = styled.div`
  max-width: 1200px;
  display: flex;
  align-items: center;
  gap: 20px;
  flex: 1;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 12px;
    text-align: center;
  }
`;

const Text = styled.p`
  margin: 0;
  font-size: 14px;
  line-height: 1.5;
  color: var(--fg);
  flex: 1;

  a {
    color: var(--primary);
    text-decoration: underline;
    
    &:hover {
      text-decoration: none;
    }
  }

  @media (max-width: 768px) {
    font-size: 13px;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 12px;
  flex-shrink: 0;

  @media (max-width: 768px) {
    width: 100%;
    flex-direction: column;
  }
`;

const Button = styled.button`
  padding: 10px 24px;
  border-radius: 12px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;

  @media (max-width: 768px) {
    width: 100%;
    padding: 12px 24px;
  }
`;

const AcceptButton = styled(Button)`
  background: var(--primary);
  color: var(--primaryFg);
  border: none;

  &:hover {
    opacity: 0.9;
    transform: translateY(-1px);
  }
`;

const DeclineButton = styled(Button)`
  background: transparent;
  color: var(--fg);
  border: 1px solid var(--bdr);

  &:hover {
    background: var(--card);
    border-color: var(--primary);
  }
`;

const COOKIE_CONSENT_KEY = '__astorius_cookie_consent__';

export default function CookieConsent() {
  const { t } = useTranslation();
  const [visible, setVisible] = React.useState(false);

  React.useEffect(() => {
    // Проверяем, было ли уже дано согласие
    try {
      const consent = localStorage.getItem(COOKIE_CONSENT_KEY);
      if (!consent) {
        // Показываем баннер через небольшую задержку для лучшего UX
        setTimeout(() => setVisible(true), 1000);
      }
    } catch (error) {
      console.error('Error checking cookie consent:', error);
    }
  }, []);

  const handleAccept = () => {
    try {
      localStorage.setItem(COOKIE_CONSENT_KEY, 'accepted');
      setVisible(false);
    } catch (error) {
      console.error('Error saving cookie consent:', error);
    }
  };

  const handleDecline = () => {
    try {
      localStorage.setItem(COOKIE_CONSENT_KEY, 'declined');
      setVisible(false);
    } catch (error) {
      console.error('Error saving cookie consent:', error);
    }
  };

  if (!visible) return null;

  return (
    <Banner>
      <Content>
        <Text>
          {t('cookieConsent.message', 'Мы используем файлы cookie для улучшения работы сайта и анализа трафика. Продолжая использовать сайт, вы соглашаетесь с нашей')}{' '}
          <a href="/privacy" target="_blank" rel="noopener noreferrer">
            {t('cookieConsent.privacyPolicy', 'Политикой конфиденциальности')}
          </a>
        </Text>
        <ButtonGroup>
          <AcceptButton onClick={handleAccept}>
            {t('cookieConsent.accept', 'Принять')}
          </AcceptButton>
          <DeclineButton onClick={handleDecline}>
            {t('cookieConsent.decline', 'Отклонить')}
          </DeclineButton>
        </ButtonGroup>
      </Content>
    </Banner>
  );
}
